import {useEffect, useState, useRef} from "react";
import {DateTime} from "luxon";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    type UserDefinedObjectTemplateAdditionalColumn,
    UserDefinedObjectTemplateAdditionalColumnSourceType as Source,
    UserDefinedObjectTemplateAdditionalColumnType as Type,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {
    formatUserDefinedObjectAdditionalColumnValue,
    parseUserDefinedObjectAdditionalColumnInput,
    type UserDefinedObjectColumnValueContext,
} from "~/domainModels/userDefinedObjectAdditionalColumnValues";

const referenceTypes = new Set([
    Type.user,
    Type.userTemplate,
    Type.ou,
    Type.ous,
    Type.product,
    Type.userDefinedObject,
    Type.userDefinedObjects,
    Type.geographyCountries,
    Type.geographyIndiaCities,
    Type.geographyIndiaPincodes,
    Type.geographyIndiaStates,
    Type.geographyIndiaZones,
]);
const numberTypes = new Set([Type.number, Type.integer, Type.geographyLatitude, Type.geographyLongitude]);

function inputText(column: UserDefinedObjectTemplateAdditionalColumn, value: unknown): string {
    if (value === undefined || value === null) {
        return "";
    }
    if (column.type === Type.json) {
        return JSON.stringify(value, null, 2) ?? "";
    }
    if (column.type === Type.timestamp && typeof value === "string") {
        const timestamp = DateTime.fromISO(value);
        return timestamp.isValid ? timestamp.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSS") : "";
    }
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : "";
}

/** Shared field for core UDO columns. Resolver-backed fields always render as read-only values. */
export function UserDefinedObjectAdditionalColumnField({
    column,
    value,
    context,
    options,
    onChange,
    onValidityChange,
    disabled = false,
    readOnly = false,
}: {
    column: UserDefinedObjectTemplateAdditionalColumn;
    value: unknown;
    context?: UserDefinedObjectColumnValueContext;
    options?: ReadonlyArray<{id: Uuid; displayName: string}>;
    onChange?: (value: unknown) => void;
    /** Form owners use this to block their submit action while a draft is invalid. */
    onValidityChange?: (valid: boolean) => void;
    disabled?: boolean;
    readOnly?: boolean;
}) {
    const [draft, setDraft] = useState(() => inputText(column, value));
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null>(null);
    const validityCallback = useRef(onValidityChange);
    useEffect(() => {
        validityCallback.current = onValidityChange;
    }, [onValidityChange]);
    useEffect(() => {
        inputRef.current?.setCustomValidity("");
        setDraft(inputText(column, value));
        setError(null);
        validityCallback.current?.(true);
    }, [column.id, column.type, value]);

    if (readOnly || onChange === undefined || column.source.type === Source.resolver) {
        return <span>{formatUserDefinedObjectAdditionalColumnValue(column, value, context)}</span>;
    }
    const commit = (input: string | ReadonlyArray<string>, element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        const parsed = parseUserDefinedObjectAdditionalColumnInput(column, input);
        element.setCustomValidity(parsed.success ? "" : parsed.message);
        const valid = parsed.success && element.validity.valid;
        setError(valid ? null : parsed.success ? element.validationMessage : parsed.message);
        onValidityChange?.(valid);
        if (valid && parsed.success) {
            onChange(parsed.value);
        }
    };
    const common = {
        "data-udo-column-input": true,
        ref: (element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null) => {
            inputRef.current = element;
        },
        "aria-label": column.displayName,
        "aria-invalid": error !== null,
        disabled,
        required: column.source.required,
    };
    const errorDisplay = error === null ? null : <div role="alert">{error}</div>;
    if (referenceTypes.has(column.type)) {
        const multiple = column.type === Type.ous || column.type === Type.userDefinedObjects;
        const references =
            column.type === Type.userDefinedObject || column.type === Type.userDefinedObjects
                ? context?.referenceOptionsByTemplateId?.get(column.configuration.userDefinedObjectTemplateId)
                : undefined;
        const choices = options ?? references ?? [];
        const selected = multiple
            ? Array.isArray(value)
                ? value.filter((id): id is string => typeof id === "string")
                : []
            : typeof value === "string"
              ? value
              : "";
        return (
            <div>
                <select
                    {...common}
                    multiple={multiple}
                    value={selected}
                    onChange={(event) => {
                        commit(
                            multiple ? Array.from(event.currentTarget.selectedOptions, (option) => option.value) : event.currentTarget.value,
                            event.currentTarget,
                        );
                    }}
                >
                    {multiple ? null : <option value="">Select an option</option>}
                    {choices.map((option) => (
                        <option
                            key={option.id}
                            value={option.id}
                        >
                            {option.displayName}
                        </option>
                    ))}
                </select>
                {errorDisplay}
            </div>
        );
    }
    if (column.type === Type.boolean) {
        return (
            <div>
                <select
                    {...common}
                    value={draft}
                    onChange={(event) => {
                        setDraft(event.currentTarget.value);
                        commit(event.currentTarget.value, event.currentTarget);
                    }}
                >
                    <option value="">Select an option</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>
                {errorDisplay}
            </div>
        );
    }
    if (column.type === Type.json) {
        return (
            <div>
                <textarea
                    {...common}
                    value={draft}
                    onChange={(event) => {
                        setDraft(event.currentTarget.value);
                        commit(event.currentTarget.value, event.currentTarget);
                    }}
                />
                {errorDisplay}
            </div>
        );
    }
    const type =
        column.type === Type.timestamp
            ? "datetime-local"
            : numberTypes.has(column.type)
              ? "number"
              : column.type === Type.email
                ? "email"
                : column.type === Type.indianPhoneNumber
                  ? "tel"
                  : "text";
    return (
        <div>
            <input
                {...common}
                type={type}
                step={column.type === Type.timestamp ? "0.001" : column.type === Type.integer ? 1 : type === "number" ? "any" : undefined}
                value={draft}
                onChange={(event) => {
                    const text = event.currentTarget.value;
                    setDraft(text);
                    const input = column.type === Type.timestamp && text !== "" ? (DateTime.fromISO(text).toISO() ?? text) : text;
                    commit(input, event.currentTarget);
                }}
            />
            {errorDisplay}
        </div>
    );
}

/** Validate only the editors in this form/panel before a mutation, including drafts not yet committed to state. */
export function useUserDefinedObjectFieldValidation() {
    const fieldsRef = useRef<HTMLDivElement | null>(null);
    const validateFields = () => {
        const inputs = fieldsRef.current?.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-udo-column-input]") ?? [];
        for (const input of inputs) {
            if (!input.reportValidity()) {
                return false;
            }
        }
        return true;
    };
    return {fieldsRef, validateFields};
}
